import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, AccountBalance, TrendingDown } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { ExpenseService } from '../services/expenseService';
import { Expense } from '../types';

interface ExpenseFormData {
  category: Expense['category'];
  amount: number;
  description: string;
  date: string;
}

const EXPENSE_CATEGORIES = [
  'rent',
  'utilities',
  'supplies',
  'salary',
  'marketing',
  'equipment',
  'maintenance',
  'insurance',
  'taxes',
  'other',
] as const;

export default function Expenses() {
  const { appUser } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { control, handleSubmit, reset, formState: { errors } } = useForm<ExpenseFormData>({
    defaultValues: {
      category: 'other',
      date: new Date().toISOString().split('T')[0], // Today's date
    },
  });

  useEffect(() => {
    const loadExpenses = async () => {
      if (!appUser?.salonId) return;

      try {
        const expensesData = await ExpenseService.getExpenses(appUser.salonId);
        setExpenses(expensesData);
      } catch (error) {
        console.error('Error loading expenses:', error);
      }
    };

    loadExpenses();
  }, [appUser]);

  const handleOpenDialog = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      reset({
        category: expense.category,
        amount: expense.amount,
        description: expense.description,
        date: expense.date.toISOString().split('T')[0],
      });
    } else {
      setEditingExpense(null);
      reset({
        category: 'other',
        date: new Date().toISOString().split('T')[0],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
    reset();
  };

  const onSubmit = async (data: ExpenseFormData) => {
    if (!appUser?.salonId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const expenseData = {
        category: data.category,
        amount: Number(data.amount),
        description: data.description,
        date: new Date(data.date),
      };

      if (editingExpense) {
        // Update existing expense
        await ExpenseService.updateExpense(editingExpense.id, expenseData);
        setSuccess('Expense updated successfully!');
      } else {
        // Add new expense
        await ExpenseService.addExpense(appUser.salonId, expenseData);
        setSuccess('Expense added successfully!');
      }

      // Refresh expenses list
      const updatedExpenses = await ExpenseService.getExpenses(appUser.salonId);
      setExpenses(updatedExpenses);

      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await ExpenseService.deleteExpense(expenseId);
      setExpenses(expenses.filter(expense => expense.id !== expenseId));
      setSuccess('Expense deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete expense');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      rent: 'error',
      utilities: 'warning',
      supplies: 'primary',
      salary: 'success',
      marketing: 'secondary',
      equipment: 'primary',
      maintenance: 'warning',
      insurance: 'error',
      taxes: 'error',
      other: 'secondary',
    };
    return colors[category] || 'secondary';
  };

  const getTotalExpenses = () => {
    return expenses.reduce((total, expense) => total + expense.amount, 0);
  };

  const getMonthlyExpenses = () => {
    const currentMonth = new Date();
    currentMonth.setDate(1);
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    return expenses
      .filter(expense => expense.date >= currentMonth && expense.date < nextMonth)
      .reduce((total, expense) => total + expense.amount, 0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" gutterBottom>
            Expense Tracking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track business expenses to monitor profitability and cash flow.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Expense
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Quick Stats */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Expenses
              </Typography>
              <Typography variant="h4" color="error.main">
                ₦{getTotalExpenses().toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                This Month
              </Typography>
              <Typography variant="h4" color="error.main">
                ₦{getMonthlyExpenses().toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4" color="primary">
                {expenses.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Avg per Expense
              </Typography>
              <Typography variant="h4" color="secondary">
                ₦{expenses.length > 0 ? (getTotalExpenses() / expenses.length).toLocaleString() : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expenses Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Expense Records
          </Typography>

          {expenses.length === 0 ? (
            <Box textAlign="center" py={4}>
              <AccountBalance sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No expenses recorded yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Start tracking your business expenses to monitor profitability.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Add First Expense
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>
                        {expense.date.toLocaleDateString()}
                      </TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={expense.category}
                          size="small"
                          color={getCategoryColor(expense.category)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>₦{expense.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleOpenDialog(expense)}
                          color="secondary"
                          size="small"
                          title="Edit"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteExpense(expense.id)}
                          color="error"
                          size="small"
                          title="Delete"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select {...field} label="Category">
                        {EXPENSE_CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Amount"
                      type="number"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: 'Date is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Date"
                      type="date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={!!errors.date}
                      helperText={errors.date?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Quick amounts for common expenses:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {[5000, 10000, 15000, 20000, 50000].map((amount) => (
                    <Chip
                      key={amount}
                      label={`₦${amount.toLocaleString()}`}
                      size="small"
                      variant="outlined"
                      onClick={() => reset({ ...control._formValues, amount })}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      placeholder="Describe the expense (e.g., Monthly shop rent, Hair products purchase, Generator fuel)"
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingExpense ? 'Update Expense' : 'Add Expense')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
